import React, { Suspense, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ResourceDetailContext } from 'app/components/Resources/ResourceDetail/ResourceDetailContext';
import ResourceOnboardingSequenceStepOne from 'app/components/Resources/ResourceDetail/ResourceOnboardingSequence/ResourceOnboardingSequenceStepOne';
import useResourceOnboardingSequence from 'app/components/Resources/hooks/useResourceOnboardingSequence';
import { FullHeartIcon } from 'app/components/shared/icons';
import useBehavioral from 'hooks/useBehavioral';
import useDebounceFunction from 'hooks/useDebounceFunction';
import useIsMounted from 'hooks/useIsMounted';
import useReaction, { ReactionEventObject } from 'hooks/useReaction';
import useUserData from 'hooks/useUserData';
import { addToast } from 'store/reducers/toast-reducer';
import { singularOrPlural } from 'utils';

import { IShareBarType } from '..';
import { HomeBaseDrawerWrapper } from '../../HomeDrawers/HomeBaseDrawer/styled';
import SideDrawer from '../../SideDrawer';
import ShareBarLikeDrawer from '../ShareBarLikeDrawer';
import useShareBar from '../useShareBar';
import { ShareBarLikeCTAText, ShareBarLikeCTAWrapper } from './styled';

interface ShareBarLikeCTAProps {
  type: IShareBarType;
  slug: string;
  hideOnboarding?: boolean;
  children?: (props: { text: string | number }) => React.ReactNode;
}

export default function ShareBarLikeCTA({
  type,
  slug,
  hideOnboarding = false,
  children,
}: Readonly<ShareBarLikeCTAProps>) {
  const { showOnboarding, currentStep, setShowOnboarding } = useResourceOnboardingSequence();
  const isMounted = useIsMounted();
  const { push } = useHistory();
  const shouldRequest = useRef<boolean>(true);
  const dispatch = useDispatch();
  const userData = useUserData();
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [liked, setLiked] = useState<boolean>(false);
  const query = useShareBar(slug, type, {
    onSuccess: (data) => {
      if (data.reactions.find((reaction) => reaction.person.uuid === userData?.uuid)) {
        setLiked(true);
      }
    },
  });
  const context = useContext(ResourceDetailContext);
  const { data } = query;
  const reactionsListOpenedBehavioral = useBehavioral('REACTIONS_LIST_OPENED');

  const handleReactionEvent = (event: MessageEvent<ReactionEventObject>) => {
    shouldRequest.current = false;
    setLiked(event.data.active);
  };

  const { addReaction, removeReaction } = useReaction(type, slug, 'like', handleReactionEvent);

  const handleLikesClick = () => {
    push(`/person/${userData?.slug}#likes_drawer`);
  };

  const handleReaction = () => {
    if (!isMounted) return;
    if (!shouldRequest.current) {
      shouldRequest.current = true;
      return;
    }

    if (!liked) {
      removeReaction(() => query.refetch());
    } else {
      dispatch(
        addToast({
          message: (
            <span>
              Added to your{' '}
              <u style={{ cursor: 'pointer' }} onClick={handleLikesClick}>
                Likes
              </u>
            </span>
          ),
          type: 'success',
        })
      );
      addReaction(() => query.refetch());
    }
    shouldRequest.current = true;
  };

  const imIncludedInResponse = useMemo<boolean>(() => {
    if (data) {
      return (
        data.reactions.find((reaction) => reaction.person.uuid === userData?.uuid) !== undefined
      );
    }

    return false;
  }, [data]);

  useEffect(() => {
    if (imIncludedInResponse) {
      setShowOnboarding(false);
    }
  }, [imIncludedInResponse]);

  useDebounceFunction(liked, handleReaction);

  const text = useMemo(() => {
    if (data) {
      if (
        liked &&
        (data.reactions.length === 0 || (imIncludedInResponse && data.reactions.length === 1))
      ) {
        return 'You';
      }

      if (imIncludedInResponse && !liked) {
        return data.reactions.length - 1;
      }

      if (!liked && data.reactions.length > 0) {
        return data.reactions.length;
      }

      if (
        liked &&
        (data.reactions.length === 1 || (data.reactions.length === 2 && imIncludedInResponse))
      ) {
        const notMe = data.reactions.find((reaction) => reaction.person.uuid !== userData?.uuid);
        return `You and ${notMe.person.full_name}`;
      }

      if (liked && data.reactions.length > 1 && imIncludedInResponse) {
        return `You and ${data.reactions.length - 1} ${singularOrPlural(
          data.reactions.length - 1,
          'other',
          'others'
        )}`;
      }

      if (liked && data.reactions.length > 1) {
        return `You and ${data.reactions.length} ${singularOrPlural(
          data.reactions.length,
          'other',
          'others'
        )}`;
      }

      return 'Like';
    }
  }, [data, liked, imIncludedInResponse]);

  const handleLikeCick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    if (context && context.onboardingStep === 1) {
      context.setOnboardingStep(2);
    }
    shouldRequest.current = true;
    setLiked(!liked);
  };

  const handleTextClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    reactionsListOpenedBehavioral();
    setShowDrawer(true);
  };

  return (
    <>
      <ShareBarLikeCTAWrapper data-liked={liked} liked={liked} onClick={handleLikeCick}>
        {!hideOnboarding && currentStep && currentStep === 1 && showOnboarding && !liked ? (
          <ResourceOnboardingSequenceStepOne />
        ) : null}
        <FullHeartIcon
          width={24}
          height={24}
          className={liked ? 'share-bar-heart liked' : 'share-bar-heart'}
          fill={liked ? '#D47563' : 'none'}
          border={liked ? '#D47563' : 'var(--color-primary)'}
        />
        <ShareBarLikeCTAText onClick={handleTextClick}>
          {children ? children({ text }) : text}
        </ShareBarLikeCTAText>
      </ShareBarLikeCTAWrapper>
      {showDrawer ? (
        <Suspense fallback={<></>}>
          <SideDrawer closeDrawer={() => setShowDrawer(false)} withClose>
            <Suspense fallback={<HomeBaseDrawerWrapper />}>
              <ShareBarLikeDrawer query={query} />
            </Suspense>
          </SideDrawer>
        </Suspense>
      ) : null}
    </>
  );
}
